<template>
  <div
    v-scrollable="{
      heightThresholdAdjust: loadMoreBottomThreshold,
      attachToWindow: attachScrollToWindow,
      customParentElement: scrollableParentElement
    }"
    class="base-list"
    @reached-bottom="onReachedBottom"
  >
    <div v-if="showSearch && mode === 'pagination'">
      <input
        v-model="search"
        type="text"
        class="form-control mb-3"
        :placeholder="searchLabel"
      >
      <hr class="border-muted">
    </div>

    <div v-if="requestFailed">
      <strong>{{ errorLabel }}</strong>
      <ButtonMore
        :loading="false"
        :label="tryAgainLabel"
        @click="$emit('reload')"
      />
    </div>
    <div
      v-else
      class="base-list__content"
      :style="{height: fullHeight ? '100%': 'auto' }"
    >
      <template v-if="items && items.length === 0 && !loading">
        <slot name="nodata">
          <base-text
            size="s"
            light
          >
            {{ noDataLabel }}
          </base-text>
          <ButtonMore
            v-if="showReload"
            :label="reloadLabel"
            :caret="false"
            @click="$emit('reload')"
          />
        </slot>
      </template>

      <template v-else>
        <template v-if="mode !== 'pagination' || !loading">
          <div>
            <template
              v-for="(item, index) in items"
            >
              <!-- @slot Default -->
              <slot
                :item="item"
                :index="index"
              >
                <!-- @slot Use this slot cell -->
                <slot
                  name="cell"
                  :item="item"
                  :index="index"
                >
                  <div :key="index">
                    Element #{{ index }}
                  </div>
                </slot>
              </slot>
            </template>
          </div>
        </template>
      </template>

      <template v-if="loading">
        <slot name="loading">
          <base-loading :show="loading" />
        </slot>
      </template>

      <template v-if="mode === 'loadMore' && !loading">
        <!-- @slot Use this slot loadMore -->
        <slot name="loadMore">
          <div v-if="items.length !== 0 && showLoadMore">
            <b-row class="justify-content-md-center">
              <div v-if="showLoadMore">
                <ButtonMore
                  v-if="items.length < count"
                  :label="loadMoreLabel"
                  @click="$emit('load-more')"
                />
                <base-text v-else>
                  {{ loadMoreEndedLabel }}
                </base-text>
              </div>
            </b-row>
          </div>
        </slot>
      </template>

      <template v-else-if="mode === 'pagination'">
        <slot name="pagination">
          <BasePagination
            :value="truePage"
            :count="count"
            :page-size="pageSize"
            @pageChange="$emit('pageChange', $event)"
          />
        </slot>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BaseList',
  props: {
    items: {
      type: Array,
      required: false,
      default: () => [],
    },
    /**
         * List mode to iterate
         * @values "pagination", "loadMore"
         */
    mode: {
      type: String,
      required: false,
      default: 'pagination',
      validator: (value) => ['pagination', 'loadMore'].includes(value),
    },
    page: {
      type: Number,
      required: false,
      default: 1,
    },
    count: {
      type: Number,
      required: false,
      default: 1,
    },
    showSearch: {
      type: Boolean,
      required: false,
      default: false,
    },
    noDataLabel: {
      type: String,
      required: false,
      default: 'No data!',
    },
    loadMoreLabel: {
      type: String,
      required: false,
      default: 'Load more',
    },
    loadMoreEndedLabel: {
      type: String,
      required: false,
      default: '',
    },
    reloadLabel: {
      type: String,
      required: false,
      default: 'Reload',
    },
    searchLabel: {
      type: String,
      required: false,
      default: 'Search',
    },
    loadingLabel: {
      type: String,
      required: false,
      default: 'Loading',
    },
    errorLabel: {
      type: String,
      required: false,
      default: 'There was an error loading data.',
    },
    tryAgainLabel: {
      type: String,
      required: false,
      default: 'Try again',
    },
    pageSize: {
      type: Number,
      default: 10,
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    requestFailed: {
      type: Boolean,
      required: false,
      default: false,
    },
    showLoadMore: {
      type: Boolean,
      required: false,
      default: false,
    },
    showReload: {
      type: Boolean,
      default: false,
    },
    loadMoreBottomThreshold: {
      type: Number,
      default: 0,
    },
    attachScrollToWindow: {
      type: Boolean,
      default: false,
    },
    scrollableParentElement: {
      type: String,
      default: null,
    },
    fullHeight: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      search: '',
    };
  },
  computed: {
    truePage() {
      return this.$route.query.page ? parseInt(this.$route.query.page, 10) : this.page;
    },
  },
  methods: {
    onReachedBottom() {
      if (this.mode === 'loadMore' && !this.loading) this.$emit('reached-bottom');
    },
  },
};
</script>

<style lang="scss" scoped>
.base-list {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    &__content {
        display: flex;
        flex-direction: column;
        flex: 1;
    }
}
</style>
